import { URLS } from '@/lib/constants';
import { router, type Href } from 'expo-router';

/**
 * Send the app to the top (root) of the stack that owns `href` — the standard
 * "tap the active tab / tap the logo again" behaviour that every app has.
 *
 * Built on Expo Router's `dismissTo`, which is the equivalent of React
 * Navigation's `StackActions.popToTop()` but targeted at a specific screen:
 *
 *   - Deeper inside that stack → pops back to the existing root screen
 *     (no new screen, no back button, no remount flash).
 *   - Already at that root      → no-op.
 *   - `href` not in the stack   → replaces the current screen with it.
 *
 * Reusable for any stack root, e.g. `resetToRoot('/library')` down the line.
 */
export function resetToRoot(href: Href): void {
  router.dismissTo(href);
}

/** The app's home = the courses catalog root. */
export const HOME_HREF = URLS.APP_HOME as Href;

/** Convenience wrapper: send the app to the home (courses catalog) root. */
export function goHome(): void {
  resetToRoot(HOME_HREF);
}

/**
 * Back-button behaviour for a Stack `header` renderer, reusable by any section.
 *
 * A section reached by `push` (e.g. a hidden-tab route like `/instructors`) has
 * no stack-level `back` at its own root, yet the global history can still go
 * back to wherever we came from. This resolves both cases uniformly so every
 * new `/section` gets a working back button for free:
 *
 *   header: ({ navigation, back }) => {
 *     const { showBack, onBack } = resolveHeaderBack(navigation, back)
 *     return <ResponsiveHeader showBack={showBack} onBack={onBack} ... />
 *   }
 */
export function resolveHeaderBack(
  navigation: { goBack: () => void },
  back: unknown,
): { showBack: boolean; onBack: () => void } {
  const hasStackBack = !!back;
  return {
    showBack: hasStackBack || router.canGoBack(),
    onBack: () => (hasStackBack ? navigation.goBack() : router.back()),
  };
}
